import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Languages } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { track } from '../services/analytics';
import { MegaMenuDesktop, MegaMenuMobile } from './MegaMenu';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { lang, setLang, t } = useLanguage();

  const toggleLang = () => {
    const next = lang === 'en' ? 'hi' : 'en';
    track('cta_language_toggled', { from: lang, to: next });
    setLang(next);
  };

  const LangSwitch = ({ className = '' }) => (
    <button
      data-testid="language-toggle"
      onClick={toggleLang}
      aria-label={t('nav.lang_label')}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-xs font-semibold text-gray-700 hover:border-blue-600 hover:text-blue-600 transition-colors ${className}`}
    >
      <Languages size={14} />
      <span>{lang === 'en' ? 'EN' : 'हिं'}</span>
      <span className="text-gray-300">|</span>
      <span className="text-gray-400">{lang === 'en' ? 'हिं' : 'EN'}</span>
    </button>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <div className="bg-blue-600 rounded-lg px-4 py-2">
              <span className="text-white font-bold text-xl">RentKollect</span>
              <p className="text-white text-[10px] leading-tight">{t('nav.tagline')}</p>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-6">
            <MegaMenuDesktop />
            <a
              href="/#managers"
              className="text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium"
            >
              {t('nav.for_managers')}
            </a>
            <LangSwitch />
            <Button
              data-testid="navbar-login-btn"
              onClick={() => { track('cta_login_clicked', { surface: 'navbar', viewport: 'desktop' }); window.location.href = '/manager/login'; }}
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg px-6"
            >
              {t('nav.login')}
            </Button>
            <Button
              data-testid="navbar-signup-btn"
              onClick={() => { track('cta_signup_clicked', { surface: 'navbar', viewport: 'desktop' }); window.location.href = '/manager/login'; }}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6"
            >
              {t('nav.signup')}
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center gap-2">
            <LangSwitch />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-700 hover:text-blue-600 p-2"
              aria-label="Toggle menu"
              data-testid="navbar-mobile-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t" data-testid="navbar-mobile-menu">
          <div className="px-4 py-4 space-y-1">
            <MegaMenuMobile onNavigate={() => setMobileMenuOpen(false)} />
            <a
              href="/#managers"
              className="block text-gray-700 hover:text-blue-600 transition-colors py-3"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.for_managers')}
            </a>
            <div className="pt-2 space-y-2">
              <Button
                onClick={() => { track('cta_login_clicked', { surface: 'navbar', viewport: 'mobile' }); window.location.href = '/manager/login'; }}
                variant="outline"
                className="w-full border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg"
              >
                {t('nav.login')}
              </Button>
              <Button
                onClick={() => { track('cta_signup_clicked', { surface: 'navbar', viewport: 'mobile' }); window.location.href = '/manager/login'; }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                {t('nav.signup')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
